import { useEffect, useState } from 'react';

import { Button } from '@/components/core/Button';
import { Callout } from '@/components/core/Callout';
import { Checkbox } from '@/components/core/Checkbox';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogHeaderDefault,
} from '@/components/core/Dialog';
import useAdditionalCategoryAttributeService from '@/hooks/useAdditionalCategoryAttributeService';
import usePhysicalAttributeService from '@/hooks/usePhysicalAttributeService';
import { useTranslation } from '@/hooks/useTranslation';
import { logger } from '@/services/logger';

import {
    ComparedItemsIterface,
    CompareItemsDialogProps,
} from './compare-items-dialog-types';
import {
    isAttributeHidden,
    loadAdditionalSectionAttributes,
    mergeAttributes,
    prepareAttributeTemplates,
    toJson,
    ValueToString,
} from './compare-items-dialog-utils';
import CompareItemsDialogTable from './CompareItemsDialogTable';

const CompareItemsDialog = ({
    items,
    rootScope,
    currentLayout,
    onClose,
}: CompareItemsDialogProps) => {

    const [open] = useState(true);
    const [isShowDifferentOnly, setIsShowDiffrentOnly] = useState(false);
    const [isShowFilledOnly, setIsShowFilledOnly] = useState(true);
    const [itemsWithDifferingCategories, setItemsWithDifferingCategories] =
        useState(false);
    const [comparedItems, setComparedItems] = useState<ComparedItemsIterface[]>(
        [],
    );

    const { t } = useTranslation();
    const { additionalCategoryAttributeService } =
        useAdditionalCategoryAttributeService();
    const { physicalAttributeService } = usePhysicalAttributeService();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const firstItem = toJson(items[0]);
                const secondItem = toJson(items[1]);
                if (firstItem.category__ !== secondItem.category__) {
                    setItemsWithDifferingCategories(true);
                }
                if (
                    additionalCategoryAttributeService &&
                    physicalAttributeService
                ) {
                    const response1 = await loadAdditionalSectionAttributes(
                        'edit',
                        firstItem,
                        additionalCategoryAttributeService,
                        rootScope,
                        currentLayout,
                    ).then((result) => {
                        return result;
                    });

                    const response2 = await loadAdditionalSectionAttributes(
                        'edit',
                        secondItem,
                        additionalCategoryAttributeService,
                        rootScope,
                        currentLayout,
                    ).then((result) => {
                        return result;
                    });
                    const _attributes = await mergeAttributes(
                        response1,
                        response2,
                    );
                    prepareAttributeTemplates(
                        _attributes,
                        firstItem,
                        secondItem,
                        rootScope.dataModel,
                    );
                    const _compareditems: ComparedItemsIterface[] = [];
                    _attributes.map(async (att) => {
                        if (!(isAttributeHidden(att, firstItem) || isAttributeHidden(att, secondItem))) {
                            const formattedFirstItem = ValueToString(
                                att,
                                firstItem,
                                physicalAttributeService,
                                rootScope,
                            );
                            const formattedSecondItem = ValueToString(
                                att,
                                secondItem,
                                physicalAttributeService,
                                rootScope,
                            );

                            _compareditems.push({
                                firstItem: formattedFirstItem,
                                secondItem: formattedSecondItem,
                                attribute: att,
                            });
                        }
                    });
                    if (_compareditems.length) setComparedItems(_compareditems);
                }
            } catch (error) {
                logger.error('CompareItemsDialog:', error);
            }
        };
        fetchData();
    }, [
        additionalCategoryAttributeService,
        physicalAttributeService,
        items,
        rootScope,
        currentLayout,
    ]);

    function onDialogClosed() {
        onClose();
    }
    return (
        <Dialog open={open} onOpenChange={() => onDialogClosed()}>
            <DialogContent
                onPointerDownOutside={() => onDialogClosed()}
                onInteractOutside={() => onDialogClosed()}
                variant="large"
                data-testid="compare-items-modal"
            >
                <DialogHeader>
                    <DialogHeaderDefault>
                        {t('ITEM.DETAILED_COMPARISON')}
                    </DialogHeaderDefault>
                </DialogHeader>
                <DialogBody className="tw-h-[80vh] tw-overflow-y-scroll">
                    <div className="tw-w-full tw-mb-2">
                        <div className="tw-flex tw-flex-row tw-items-center">
                            <label className="tw-mr-4 tw-text-t-sm">
                                {t('ITEM.DISPLAY_OPTIONS')}:
                            </label>
                            <label className="tw-mr-4 tw-text-t-sm">
                                <Checkbox
                                    isChecked={isShowDifferentOnly}
                                    className="tw-mr-2"
                                    onClick={() =>
                                        setIsShowDiffrentOnly(
                                            !isShowDifferentOnly,
                                        )
                                    }
                                />
                                {t('ITEM.SHOW_DIFFERENCES_ONLY')}
                            </label>
                            <label>
                                <Checkbox
                                    isChecked={isShowFilledOnly}
                                    className="tw-mr-2"
                                    onClick={() =>
                                        setIsShowFilledOnly(
                                            !isShowFilledOnly,
                                        )
                                    }
                                />
                                {t('ITEM.SHOW_FILLED_ONLY')}
                            </label>
                        </div>
                    </div>
                    {itemsWithDifferingCategories && (
                        <Callout className="tw-mb-2" variant="info">
                            {t('ITEM.HINT_DIFFERING_CATEGORIES')}
                        </Callout>
                    )}
                    {CompareItemsDialogTable({
                        items,
                        comparedItems,
                        isShowDifferentOnly,
                        isShowFilledOnly,
                    })}
                </DialogBody>
                <DialogFooter>
                    <DialogClose asChild={true}>
                        <Button
                            variant="secondary"
                            onClick={onDialogClosed}
                        >
                            {t('CLOSE')}
                        </Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

CompareItemsDialog.displayName = 'CompareItemsDialog';
export { CompareItemsDialog, type CompareItemsDialogProps };
